import {
  LIBELLES_CATEGORIES,
  NOM_BOUSSOLE,
  SUJETS_BOUSSOLE,
  repondreScripte,
  type CategorieDossier,
  type ModeBoussole,
} from '@repere/core';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useSession } from '../auth/session';
import { Bouton } from '../ui';

interface ReponseBoussole {
  mode: ModeBoussole;
  reponse: string;
  sujet: string | null;
  categorie: CategorieDossier | null;
  suggestions: { id: string; titre: string }[];
  jetonsRestants: number;
}

interface Echange {
  question: string;
  reponse: ReponseBoussole;
}

/** Les trois entrées proposées avant qu'on ait posé quoi que ce soit. */
const AMORCES = ['qui-a-enfant', 'declarer-depense', 'demander-echange']
  .map((id) => SUJETS_BOUSSOLE.find((sujet) => sujet.id === id))
  .filter((sujet) => sujet !== undefined);

/** Réponse écrite, calculée ici même, quand l'API est injoignable. */
function repliLocal(question: string): ReponseBoussole {
  const scripte = repondreScripte(question);
  return {
    mode: 'guide',
    reponse: scripte.reponse,
    sujet: scripte.sujet?.id ?? null,
    categorie: scripte.sujet?.categorie ?? null,
    suggestions: scripte.suggestions.map((sujet) => ({ id: sujet.id, titre: sujet.titre })),
    jetonsRestants: 0,
  };
}

/**
 * Rose des vents : un cercle, et l'aiguille qui le traverse.
 *
 * Exportée : c'est aussi l'icône de l'entrée « Aide » du menu (`MenuCompte.tsx`), qui
 * ouvre cette même fenêtre. Deux dessins pour une seule porte auraient fini par diverger.
 */
export function RoseDesVents() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="m15.5 8.5-2 5-5 2 2-5z" />
    </svg>
  );
}

/**
 * Boussole — l'aide en ligne, posée dans la coquille.
 *
 * Trois partis, et ils tiennent ensemble :
 *
 * - **Elle ne sait rien du dossier**, et le dit dès l'ouverture. Un parent doit pouvoir
 *   demander « comment je conteste une dépense » sans se demander si sa question sera
 *   lisible par l'autre. Rien n'est envoyé d'autre que la phrase tapée.
 * - **Rien n'est conservé**, ni ici ni sur le serveur. La conversation vit le temps de
 *   l'onglet ; la fermer l'efface. Ce n'est pas un oubli d'implémentation : une
 *   conversation avec l'assistance n'est pas une pièce du dossier.
 * - **Elle répond même quand rien ne marche.** Si l'API est injoignable, la réponse est
 *   calculée ici, à partir de la même base que le serveur. C'est la troisième et dernière
 *   marche du repli — après le budget de jetons, après la panne du fournisseur — et elle
 *   fait qu'un écran d'aide ne peut pas afficher un message d'erreur.
 *
 * Elle ne se montre qu'à quelqu'un de connecté : l'aide de qui n'arrive pas à ouvrir sa
 * session vit sur la vitrine, où elle ne coûte rien.
 *
 * **Elle n'a plus de bouton à elle.** Une pastille « Aide » flottait au-dessus du coin bas
 * droit de tous les écrans : sur téléphone elle recouvrait la fin des cartes, et elle se
 * posait exactement là où la barre des catégories du dossier demande le pouce. Elle est
 * devenue une entrée du menu de l'en-tête, comme l'urgence avant elle — l'aide est un
 * geste, pas un écran, et le menu est la porte des gestes. C'est donc `MenuCompte` qui la
 * monte, et elle ne se rend que le temps qu'on la garde ouverte.
 */
export function Boussole({ onFermer }: { onFermer: () => void }) {
  const { client, profil } = useSession();
  const dialogue = useRef<HTMLDialogElement>(null);
  const champ = useRef<HTMLTextAreaElement>(null);
  const [question, setQuestion] = useState('');
  const [echanges, setEchanges] = useState<Echange[]>([]);
  const [enCours, setEnCours] = useState(false);

  // Montée, elle est ouverte : c'est le menu qui décide de la rendre ou non. `showModal`
  // peut manquer (jsdom) : la fenêtre reste alors un bloc ordinaire plutôt que de faire
  // échouer le rendu.
  useEffect(() => {
    const element = dialogue.current;
    if (!element) return;
    if (!element.open) element.showModal?.();
    champ.current?.focus();
  }, []);

  if (!profil) return null;

  const demander = async (texte: string): Promise<void> => {
    const propre = texte.trim();
    if (propre === '' || enCours) return;

    setQuestion('');
    setEnCours(true);
    try {
      const reponse = await client.request<ReponseBoussole>('/support/questions', {
        method: 'POST',
        body: { question: propre },
      });
      setEchanges((precedents) => [...precedents, { question: propre, reponse }]);
    } catch {
      // Une aide qui répond « une erreur est survenue » n'aide personne : on sert la
      // réponse écrite, qui est de toute façon celle que le serveur aurait renvoyée.
      setEchanges((precedents) => [
        ...precedents,
        { question: propre, reponse: repliLocal(propre) },
      ]);
    } finally {
      setEnCours(false);
      champ.current?.focus();
    }
  };

  // La conversation ne survit pas à la fermeture. Voir l'en-tête du fichier : démontée par
  // le menu, elle repart vide au prochain appel — il n'y a rien à effacer à la main.
  const fermer = (): void => onFermer();

  return (
    <dialog
      className="boussole"
      ref={dialogue}
      aria-labelledby="boussole-titre"
      onClose={fermer}
      onClick={(evenement) => {
        if (evenement.target === dialogue.current) dialogue.current?.close();
      }}
    >
      <div className="boussole-entete">
        <h2 id="boussole-titre">
          <RoseDesVents />
          {NOM_BOUSSOLE}
        </h2>
        <Bouton variante="lien" onClick={() => dialogue.current?.close() ?? fermer()}>
          Fermer
        </Bouton>
      </div>

      <p className="boussole-cadre discret">
        J’explique comment se servir de Repère. Je ne vois rien de votre dossier, et cette
        conversation n’est conservée nulle part.
      </p>

      <div className="boussole-fil" aria-live="polite">
        {echanges.map((echange, rang) => (
          <div className="boussole-echange" key={`${rang}-${echange.question}`}>
            <p className="boussole-question">{echange.question}</p>
            <div className="boussole-reponse">
              <p>{echange.reponse.reponse}</p>
              {echange.reponse.categorie ? (
                <p className="boussole-ou discret">
                  Cela se passe dans « {LIBELLES_CATEGORIES[echange.reponse.categorie]} ».
                </p>
              ) : null}
              {echange.reponse.mode === 'guide' ? (
                <p className="boussole-source discret">Réponse issue de mes fiches d’aide.</p>
              ) : null}
            </div>
            {echange.reponse.suggestions.length > 0 ? (
              <ul className="boussole-suggestions">
                {echange.reponse.suggestions.map((sujet) => (
                  <li key={sujet.id}>
                    <button type="button" onClick={() => void demander(sujet.titre)}>
                      {sujet.titre}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ))}

        {echanges.length === 0 ? (
          <ul className="boussole-suggestions">
            {AMORCES.map((sujet) => (
              <li key={sujet.id}>
                <button type="button" onClick={() => void demander(sujet.titre)}>
                  {sujet.titre}
                </button>
              </li>
            ))}
          </ul>
        ) : null}

        {enCours ? <p className="boussole-attente discret">Je cherche…</p> : null}
      </div>

      <form
        className="boussole-saisie"
        onSubmit={(evenement: FormEvent) => {
          evenement.preventDefault();
          void demander(question);
        }}
      >
        <label className="invisible" htmlFor="boussole-question">
          Votre question
        </label>
        <textarea
          id="boussole-question"
          ref={champ}
          rows={2}
          maxLength={400}
          value={question}
          placeholder="Votre question…"
          onChange={(evenement) => setQuestion(evenement.target.value)}
          onKeyDown={(evenement) => {
            // Entrée envoie, Maj+Entrée passe à la ligne : c'est ce que fait la
            // messagerie du produit, et deux conventions différentes dans la même
            // application se paient en messages envoyés à moitié.
            if (evenement.key === 'Enter' && !evenement.shiftKey) {
              evenement.preventDefault();
              void demander(question);
            }
          }}
        />
        {/* « Envoyer » et non « Demander » : un des sujets proposés s'intitule
                « Comment demander un échange de jours ? », et deux commandes qui
                s'annoncent pareil dans la même fenêtre ne se distinguent pas à l'oreille. */}
        <Bouton type="submit" disabled={enCours || question.trim() === ''}>
          Envoyer
        </Bouton>
      </form>
    </dialog>
  );
}
